import { useEffect, useMemo } from 'react';
import { useApi } from '@paradigm/api/src';
import { TradeTapeItem } from '#/unified-rfqs/entities/drfq/trades';
import {
  useDrfqOrderbook,
  useDrfqOrders,
  useDrfqService,
} from '#/unified-rfqs/contexts/drfqs';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import {
  isBidOrder,
  isOrderExecuted,
  Order,
} from '#/unified-rfqs/entities/drfq/orders';
import { Side } from '#/unified-rfqs/entities/shared';
import useFetchedDrfq from '#/unified-rfqs/contexts/use-fetched-drfq';

export function useTakerOrderbookFetcher(
  rfqId: Drfq['id'],
  options: { shouldFetch?: boolean } = { shouldFetch: true },
) {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, getRfqOrderbook] = useApi(
    drfqService.getRfqOrderbook,
  );

  useEffect(() => {
    if (options.shouldFetch === true) getRfqOrderbook({ rfqId });
  }, [getRfqOrderbook, options.shouldFetch, rfqId]);

  return {
    isLoading,
    error: resp?.ok === false ? resp.message : '',
    asks: resp?.ok === true ? resp.data.asks : [],
    bids: resp?.ok === true ? resp.data.bids : [],
  };
}

export function useDrfqOrdersFetcher(
  rfqId: Drfq['id'],
  options = { shouldFetch: true },
) {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, getRfqOrders] = useApi(drfqService.getRfqOrders);
  useEffect(() => {
    if (options.shouldFetch) getRfqOrders({ rfqId });
  }, [getRfqOrders, options.shouldFetch, rfqId]);
  return {
    isLoading,
    orders: resp?.ok === true ? resp.data.results : [],
  };
}

export function useDrfqTradesFetcher(
  rfqId: Drfq['id'],
  options = { shouldFetch: true },
) {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, getRfqTrades] = useApi(drfqService.listTrades);
  useEffect(() => {
    if (options.shouldFetch) getRfqTrades({ rfqId });
  }, [getRfqTrades, options.shouldFetch, rfqId]);
  return {
    isLoading,
    trades: resp?.ok === true ? resp.data.results : [],
  };
}

export function useSplittedOrders(rfqId: Drfq['id']) {
  const orders = useDrfqOrders(rfqId);

  return useMemo(() => splitOrders(orders), [orders]);
}

export function splitOrders(orders: readonly Order[]) {
  const bids: Order[] = [];
  const asks: Order[] = [];
  let executedAsk: Order | null = null;
  let executedBid: Order | null = null;

  for (const order of orders) {
    if (isBidOrder(order)) {
      bids.push(order);
      if (isOrderExecuted(order)) {
        executedBid = order;
      }
    } else {
      asks.push(order);
      if (isOrderExecuted(order)) {
        executedAsk = order;
      }
    }
  }

  return { bids, asks, executedAsk, executedBid };
}

export function useDrfqTradeTapeFetcher(
  rfqId: Drfq['id'],
  options = { shouldFetch: true },
): {
  isLoading: boolean;
  trade: TradeTapeItem | null;
  errorMessage: string;
} {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, listTradeTape] = useApi(
    drfqService.listTradeTape,
  );

  useEffect(() => {
    if (options.shouldFetch) listTradeTape({ rfqId });
  }, [listTradeTape, rfqId, options.shouldFetch]);

  return {
    isLoading,
    trade: resp?.ok === true ? resp.data.results[0] ?? null : null,
    errorMessage: resp?.ok === false ? resp.message : '',
  };
}

export function useTakerExecutedRfqInfo(
  drfqId: Drfq['id'],
  takerSide: Side | null | undefined,
) {
  const { drfq } = useFetchedDrfq(drfqId);
  const ordersView = useSplittedOrders(drfqId);
  const [bids, asks] = useDrfqOrderbook(drfqId);

  if (takerSide == null || drfq?.role !== 'TAKER') {
    return {
      executedOrder: null,
      executedFromDesk: null,
      executedAccount: null,
    };
  }

  const makerDesk = takerSide === 'BUY' ? asks[0]?.desk : bids[0]?.desk;
  return {
    executedFromDesk: makerDesk ?? null,
    executedOrder: ordersView.executedBid ?? ordersView.executedAsk,
    executedAccount: drfq.account_name,
  };
}
